import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Handshake } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ORG_TYPE_OPTIONS = [
  'Early childhood provider/program',
  'Nonprofit organization',
  'Advocacy organization',
  'Government agency',
  'Philanthropic/funding organization',
  'Research institution/university',
  'Community-based organization',
  'Corporate/business partner',
  'Consultant/facilitator',
  'Parent/caregiver-led organization'
];

const PARTNERSHIP_INTEREST_OPTIONS = [
  'Advocacy and policy initiatives',
  'Leadership development programs',
  'Sponsorship opportunities',
  'Event partnership or co-hosting',
  'Workshop facilitation or training',
  'Research or data collaboration',
  'Storytelling and communications campaigns',
  'Community engagement initiatives',
  'Volunteer opportunities'
];

const SectionHeading = ({ num, title }) => (
  <div className="flex items-center gap-3 mb-5 mt-10 pb-2.5 border-b-2 border-teal-50 first:mt-0">
    <div className="w-8 h-8 rounded-full bg-teal-600 text-white text-sm font-bold flex items-center justify-center shrink-0">{num}</div>
    <h2 className="text-lg font-semibold text-teal-700" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h2>
  </div>
);

const CheckboxGrid = ({ options, selected, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
    {options.map(opt => (
      <label
        key={opt}
        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
          selected.includes(opt)
            ? 'border-teal-600 bg-teal-50'
            : 'border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40'
        }`}
        data-testid={`partner-interest-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      >
        <input
          type="checkbox"
          checked={selected.includes(opt)}
          onChange={() => {
            onChange(selected.includes(opt)
              ? selected.filter(v => v !== opt)
              : [...selected, opt]);
          }}
          className="mt-0.5 accent-teal-600"
        />
        <span className="text-slate-700 leading-snug">{opt}</span>
      </label>
    ))}
  </div>
);

const PartnerWithUs = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    title_role: '',
    email: '',
    phone: '',
    website: '',
    org_type: '',
    mission: '',
    communities_served: '',
    partnership_interests: [],
    partnership_interest_other: '',
    follow_up: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['full_name', 'organization', 'title_role', 'email', 'phone', 'org_type', 'mission', 'communities_served', 'follow_up'];
    const missing = required.filter(f => !formData[f]?.trim?.());
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.partnership_interests.length === 0 && !formData.partnership_interest_other.trim()) {
      toast.error('Please select at least one partnership interest (or describe one under "Other")');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/partner-inquiry/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Partner inquiry submitted successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Header />
        <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-slate-50 flex items-center justify-center pt-24 px-4 pb-16">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-10 text-center" data-testid="partner-inquiry-success">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Thank You for Your Interest!</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Thank you for your interest in collaborating with DC ELC. We believe strong partnerships are essential to building an equitable, sustainable, and thriving early childhood system in Washington, DC. A member of our team will be in touch soon.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8"
              data-testid="partner-inquiry-back-home"
            >
              Back to Home
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-slate-50">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-teal-600 to-emerald-700 overflow-hidden pt-24">
          <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-bl from-amber-300/10 to-transparent pointer-events-none" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/75 hover:text-white mb-5 transition-colors text-sm"
              data-testid="partner-inquiry-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <div className="flex items-center gap-3 mb-3">
              <Handshake className="w-7 h-7 text-amber-200" />
              <p className="text-amber-200 text-xs font-semibold uppercase tracking-[0.18em]">DC Early Learning Collaborative</p>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Partner With Us
            </h1>
            <p className="text-white/90 text-base leading-relaxed">
              Thank you for your interest in partnering with the DC Early Learning Collaborative (DC ELC). We believe lasting change in early childhood education happens through collaboration, shared leadership, advocacy, and collective action.
            </p>
            <p className="text-white/85 text-sm leading-relaxed mt-3">
              This form helps us learn more about your organization, interests, and how we might work together to strengthen DC's early childhood ecosystem.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-20">
          <form onSubmit={handleSubmit} data-testid="partner-inquiry-form" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-9">

            {/* Section 1: Contact Information */}
            <SectionHeading num="1" title="Your Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="full_name" className="block text-xs font-semibold text-slate-700 mb-1">Full Name <span className="text-amber-600">*</span></label>
                <Input id="full_name" name="full_name" value={formData.full_name} onChange={handleChange} required placeholder="Your full name" data-testid="partner-full-name" />
              </div>
              <div>
                <label htmlFor="organization" className="block text-xs font-semibold text-slate-700 mb-1">Organization / Business Name <span className="text-amber-600">*</span></label>
                <Input id="organization" name="organization" value={formData.organization} onChange={handleChange} required placeholder="Organization name" data-testid="partner-organization" />
              </div>
              <div>
                <label htmlFor="title_role" className="block text-xs font-semibold text-slate-700 mb-1">Job Title / Role <span className="text-amber-600">*</span></label>
                <Input id="title_role" name="title_role" value={formData.title_role} onChange={handleChange} required placeholder="e.g., Executive Director" data-testid="partner-title-role" />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">Email Address <span className="text-amber-600">*</span></label>
                <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com" data-testid="partner-email" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1">Phone Number <span className="text-amber-600">*</span></label>
                <Input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="(202) 000-0000" data-testid="partner-phone" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="website" className="block text-xs font-semibold text-slate-700 mb-1">Website or Social Media <span className="text-slate-400 font-normal">(optional)</span></label>
                <Input id="website" name="website" value={formData.website} onChange={handleChange} placeholder="https://yourorg.com or @yourhandle" data-testid="partner-website" />
              </div>
            </div>

            {/* Section 2: Organization */}
            <SectionHeading num="2" title="About Your Organization" />
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2">Which best describes your organization? <span className="text-amber-600">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ORG_TYPE_OPTIONS.map(opt => (
                  <label
                    key={opt}
                    className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                      formData.org_type === opt
                        ? 'border-teal-600 bg-teal-50'
                        : 'border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40'
                    }`}
                    data-testid={`partner-org-type-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <input
                      type="radio"
                      name="org_type"
                      value={opt}
                      checked={formData.org_type === opt}
                      onChange={handleChange}
                      className="mt-0.5 accent-teal-600"
                    />
                    <span className="text-slate-700 leading-snug">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="mission" className="block text-xs font-semibold text-slate-700 mb-1">Briefly describe your organization's mission and work. <span className="text-amber-600">*</span></label>
              <Textarea
                id="mission" name="mission"
                value={formData.mission}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Tell us about your organization's mission, focus areas, and the work you do…"
                data-testid="partner-mission"
              />
            </div>

            <div className="mt-5">
              <label htmlFor="communities_served" className="block text-xs font-semibold text-slate-700 mb-1">Which communities or populations do you primarily serve? <span className="text-amber-600">*</span></label>
              <Textarea
                id="communities_served" name="communities_served"
                value={formData.communities_served}
                onChange={handleChange}
                required
                rows={3}
                placeholder="e.g., children birth–5 in Wards 7 & 8, ECE workforce, immigrant families…"
                data-testid="partner-communities-served"
              />
            </div>

            {/* Section 3: Partnership */}
            <SectionHeading num="3" title="Partnership Interests" />
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-2">How would you like to partner or collaborate with DC ELC? <span className="text-slate-400 font-normal">(Select all that apply)</span></label>
              <CheckboxGrid
                options={PARTNERSHIP_INTEREST_OPTIONS}
                selected={formData.partnership_interests}
                onChange={(next) => setFormData(prev => ({ ...prev, partnership_interests: next }))}
              />
              <div className="mt-3">
                <label htmlFor="partnership_interest_other" className="block text-xs font-semibold text-slate-700 mb-1">Other (please describe)</label>
                <Input
                  id="partnership_interest_other" name="partnership_interest_other"
                  value={formData.partnership_interest_other}
                  onChange={handleChange}
                  placeholder="A different way you'd like to collaborate"
                  data-testid="partner-interest-other"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-xs font-semibold text-slate-700 mb-2">Would you like to schedule a follow-up conversation with the DC ELC team? <span className="text-amber-600">*</span></label>
              <div className="flex gap-3">
                {['yes', 'no'].map(opt => (
                  <label
                    key={opt}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all text-sm font-medium capitalize ${
                      formData.follow_up === opt
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-teal-400'
                    }`}
                    data-testid={`partner-follow-up-${opt}`}
                  >
                    <input
                      type="radio"
                      name="follow_up"
                      value={opt}
                      checked={formData.follow_up === opt}
                      onChange={handleChange}
                      className="accent-teal-600"
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500 leading-relaxed sm:max-w-md">
                By submitting this form, you consent to DC ELC contacting you about partnership opportunities.
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white px-8 py-6 shadow-lg"
                data-testid="partner-submit-btn"
              >
                {isSubmitting ? 'Submitting…' : 'Submit Partner Inquiry'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PartnerWithUs;
